import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { auth, db, rtdb } from "../../firebase";
import { doc, onSnapshot, updateDoc, serverTimestamp, arrayUnion, arrayRemove } from "firebase/firestore";
import { ref, set, onValue } from "firebase/database";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import { useCall } from "../../context/CallContext";
import ConversationsList from "./ConversationsList";
import MessageInput from "./MessageInput";
import ChatWindow from "./ChatWindow";
import UserProfileViewer from "../UserProfileViewer";
import Settings from "../Settings";
import BlockedUsers from "../Settings/BlockedUsers";
import ImageViewer from "../ImageViewer";
import StatusSelector from "../StatusSelector";
import LogoutButton from "../LogoutButton";
import AddContact from "../Contacts/AddContact";
import SquadsList from "./SquadsList";
import CreateSquad from "./CreateSquad";
import JoinSquad from "./JoinSquad";
import CallHistoryList from "./CallHistoryList";
import BlinkList from "./BlinkList";
import { QRCodeCanvas as QRCode } from "qrcode.react";
import { useContacts } from "../../hooks/useContacts";

// 🆔 Standard Conversation ID Helper
const getConvId = (uid1, uid2) => {
  const u1 = uid1.trim();
  const u2 = uid2.trim();
  return u1 < u2 ? `${u1}_${u2}` : `${u2}_${u1}`;
};

export default function ChatUI() {
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState("chats"); // "chats" | "squads"
  const [showCreateSquad, setShowCreateSquad] = useState(false);
  const [showJoinSquad, setShowJoinSquad] = useState(false);
  const [initialJoinCode, setInitialJoinCode] = useState("");

  // ... existing states ...
  const [showSettings, setShowSettings] = useState(false);
  const [showMyProfile, setShowMyProfile] = useState(false);
  const [typing, setTyping] = useState(false);
  const [viewImage, setViewImage] = useState(null);
  const [showStatusSelector, setShowStatusSelector] = useState(false);
  const [showBlockedUsers, setShowBlockedUsers] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [showSquadQR, setShowSquadQR] = useState(false); // 🆕 Squad QR Modal State

  const navigate = useNavigate();
  const currentUid = auth.currentUser?.uid;
  const { isDarkMode, toggleTheme } = useTheme();

  const [currentUserData, setCurrentUserData] = useState(null);
  const { initiateVoiceCall, initiateVideoCall } = useCall();
  const [installPrompt, setInstallPrompt] = useState(null);
  // AI Website URL
  const aiUrl = "https://chatiqai.netlify.app/";



  // 📖 Contacts Hook (Private Aliases)
  const { contacts, updateContact } = useContacts();

  // 📱 PWA Install Prompt Listener
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const handleInstallClick = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setInstallPrompt(null);
    }
  };
  // Sync Selected User/Squad Data (Firestore)
  useEffect(() => {
    if (!selectedUser?.uid) return;

    // IF USER (Not Squad)
    if (!selectedUser.isSquad) {
      const unsub = onSnapshot(doc(db, "users", selectedUser.uid), (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setSelectedUser((prev) => ({
            ...prev,
            username: data.username,
            photoURL: data.photoURL,
            publicKey: data.publicKey, // 👈 Sync Keys
            devices: data.devices,     // 👈 Sync Devices
            // Don't overwrite isOnline/lastActive from Firestore if we use RTDB
            status: data.status,
            showLastSeen: data.showLastSeen,
          }));
        }
      });
      return () => unsub();
    } else {
      // IF SQUAD
      const unsub = onSnapshot(doc(db, "squads", selectedUser.uid), (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setSelectedUser(prev => ({
            ...prev,
            username: data.name,
            photoURL: data.photoURL,
            ...data
          }));
        }
      });
      return () => unsub();
    }
  }, [selectedUser?.uid, selectedUser?.isSquad]);

  // 🟢 Sync Online Status (RTDB) for Selected User
  useEffect(() => {
    if (!selectedUser?.uid || selectedUser.isSquad) return;

    const statusRef = ref(rtdb, `/status/${selectedUser.uid}`);
    const unsub = onValue(statusRef, (snap) => {
      const val = snap.val();
      if (val) {
        setSelectedUser(prev => ({
          ...prev,
          isOnline: val.state === 'online',
          lastActive: val.last_changed || prev?.lastActive // Use RTDB timestamp if available
        }));
      } else {
        // If no status in RTDB, assume offline
        setSelectedUser(prev => ({ ...prev, isOnline: false }));
      }
    });

    return () => unsub();
  }, [selectedUser?.uid, selectedUser?.isSquad]);

  // 🔗 Check for Invite Code on Mount
  useEffect(() => {
    const code = sessionStorage.getItem("joinSquadCode");
    if (code) {
      setInitialJoinCode(code);
      setActiveTab('squads'); // Switch to Squads tab
      setShowJoinSquad(true);
      sessionStorage.removeItem("joinSquadCode");
    }
  }, []);

  // ⌨️ Typing Status Listener
  useEffect(() => {
    if (!selectedUser?.convId || selectedUser.isSquad) {
      setTyping(false);
      return;
    }
    const typingRef = ref(rtdb, `typing/${selectedUser.convId}/${selectedUser.uid}`);
    const unsub = onValue(typingRef, (snap) => {
      setTyping(snap.val() || false);
    });
    return () => unsub();
  }, [selectedUser?.convId, selectedUser?.uid, selectedUser?.isSquad]);
  // NOTE: I am replacing the entire component logic effectively by doing a replace on the main render structure, 
  // but to be safe with this tool, I should try to target specific blocks or rewrite the return if possible.
  // Given the complexity of inserting the Tab logic, I will rewrite the Sidebar part of the return primarily.

  const [showChatMenu, setShowChatMenu] = useState(false);

  // 🔄 Fetch Current User Data
  useEffect(() => {
    if (!currentUid) return;
    const unsub = onSnapshot(doc(db, "users", currentUid), (doc) => {
      if (doc.exists()) {
        setCurrentUserData(doc.data());
      }
    });
    return () => unsub();
  }, [currentUid]);

  // 📞 Call Handlers
  // 📞 Call Handlers
  const handleVoiceCall = () => {
    if (!selectedUser) return;
    const callId = selectedUser.convId || selectedUser.uid;
    initiateVoiceCall(callId, selectedUser.uid);
  };

  const handleVideoCall = () => {
    if (!selectedUser) return;
    const callId = selectedUser.convId || selectedUser.uid;
    initiateVideoCall(callId, selectedUser.uid);
  };

  // 🛑 Block User
  const handleBlockUser = async () => {
    if (!selectedUser || !currentUid) return;
    if (window.confirm(`Block ${selectedUser.username}? You will no longer receive messages using this account.`)) {
      try {
        await updateDoc(doc(db, "users", currentUid), {
          blockedUsers: arrayUnion(selectedUser.uid)
        });
        // 🚪 Exit Chat Immediately
        setSelectedUser(null);
        // alert("User blocked."); // Optional, clearing selection is enough feedback usually, but alert is fine.
      } catch (error) {
        console.error("Error blocking user:", error);
      }
    }
  };

  // 🧹 Clear Chat
  const clearChat = async () => {
    if (!selectedUser?.convId) return;
    if (window.confirm("Clear this chat history? Messages will be hidden for you on all devices.")) {
      try {
        // Update clearedTimestamp for ME
        await updateDoc(doc(db, "conversations", selectedUser.convId), {
          [`clearedTimestamp.${currentUid}`]: serverTimestamp()
        });
        // We do NOT navigate away, just clear logic handles the view (via ChatWindow timestamp check)
      } catch (e) {
        console.error("Clear chat failed", e);
      }
    }
  };

  // ⚠️ Report User State
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState("Spam");
  const [reportDetails, setReportDetails] = useState("");

  const submitReport = async () => {
    if (!selectedUser) return;
    try {
      await import("firebase/firestore").then(({ addDoc, collection }) => {
        addDoc(collection(db, "reports"), {
          reportedBy: currentUid,
          reportedUser: selectedUser.uid,
          reason: reportReason,
          details: reportDetails,
          timestamp: serverTimestamp()
        });
      });
      setShowReportModal(false);
      setReportDetails("");
      alert("Report submitted. Thank you.");
    } catch (e) {
      console.error("Report failed", e);
    }
  };

  // 🎨 Dynamic Theme Classes
  const bgClass = isDarkMode
    ? "bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a]"
    : "bg-gradient-to-br from-[#f0f9ff] via-[#e0f2fe] to-[#f0f9ff]";

  const textClass = isDarkMode ? "text-white" : "text-gray-800";
  const subTextClass = isDarkMode ? "text-gray-400" : "text-gray-500";
  const headerClass = isDarkMode ? "bg-white/5 border-white/5" : "bg-white/40 border-slate-200/50";

  return (
    <div className={`flex h-[100dvh] antialiased overflow-hidden xl:p-8 transition-colors duration-500 ${bgClass} ${textClass}`}>
      <aside className={`flex-col h-full z-10 relative border-r transition-all duration-300 ${isDarkMode ? 'border-white/10' : 'border-gray-200/50'} ${selectedUser ? "hidden md:flex md:w-[35%] lg:w-[30%]" : "flex w-full md:w-[35%] lg:w-[30%]"}`}>

        {/* Sidebar Header */}
        <div className="px-4 py-3 flex justify-between items-center border-b border-transparent">
          <div className="flex items-center gap-3">
            {/* ... (My Profile Icon) ... */}
            <div className="relative group cursor-pointer" onClick={() => setShowMyProfile(true)}>
              {currentUserData?.photoURL ? (
                <img src={currentUserData.photoURL} alt="Me" className="w-11 h-11 rounded-full object-cover border-2 border-white/20 transition-transform group-hover:scale-105" />
              ) : (
                <div className="w-11 h-11 bg-gradient-to-br from-gray-700 to-gray-600 rounded-full flex items-center justify-center text-white/80 shadow-inner">👤</div>
              )}
              <div className="absolute inset-0 bg-black/30 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-xs">📷</span>
              </div>
            </div>

            <div className="flex flex-col">
              <span className={`font-bold text-sm hidden sm:block tracking-wide ${textClass}`}>Me</span>
              {/* Status ... */}
              <button
                onClick={(e) => { e.stopPropagation(); setShowStatusSelector(true); }}
                className={`text-[11px] ${subTextClass} hover:text-[#00a884] transition-colors flex items-center gap-1.5 -ml-0.5`}
              >
                <span>{currentUserData?.status?.icon || "🟢"}</span>
                <span className="max-w-[100px] truncate font-medium">
                  {currentUserData?.status?.text || currentUserData?.status?.label || "Set Status"}
                </span>
              </button>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            {/* Settings & Blocked Buttons ... ... */}
            <button onClick={() => setShowSettings(true)} className={`p-2.5 rounded-full transition-all duration-300 group ${isDarkMode ? "hover:bg-white/10 text-gray-300 hover:text-white" : "hover:bg-black/5 text-gray-600 hover:text-black"}`}>
              <svg className="group-hover:rotate-180 transition-transform duration-700 ease-in-out" xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>

            {/* 🔗 Share App Button */}
            <button
              onClick={async (e) => {
                e.stopPropagation();
                const shareData = {
                  title: 'ChatIQ',
                  text: 'Join me on ChatIQ! 🚀 Secure and professional chat.',
                  url: 'https://chatme.in'
                };
                if (navigator.share) {
                  try { await navigator.share(shareData); } catch (err) { }
                } else {
                  try {
                    await navigator.clipboard.writeText(shareData.url);
                    alert("App link copied to clipboard!");
                  } catch (err) { }
                }
              }}
              title="Share App"
              className={`p-2.5 rounded-full transition-all duration-300 ${isDarkMode ? "hover:bg-white/10 text-gray-300 hover:text-indigo-400" : "hover:bg-black/5 text-gray-600 hover:text-indigo-600"}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path><polyline points="16 6 12 2 8 6"></polyline><line x1="12" y1="2" x2="12" y2="15"></line></svg>
            </button>
            <button onClick={() => setShowBlockedUsers(true)} className={`p-2.5 rounded-full transition-all duration-300 ${isDarkMode ? 'hover:bg-white/10 text-gray-300 hover:text-red-400' : 'hover:bg-black/5 text-gray-600 hover:text-red-500'}`}>
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line></svg>
            </button>
            {currentUserData?.role === 'admin' && (
              <button onClick={() => navigate('/admin')} title="Admin Panel" className={`p-2 rounded-full transition-all ${isDarkMode ? "hover:bg-white/10 text-gray-400" : "hover:bg-black/5 text-gray-500"}`}>
                <span className="text-lg">🛡️</span>
              </button>
            )}

            {/* ⬇️ Install App Button (PWA) */}
            {installPrompt && (
              <button
                onClick={handleInstallClick}
                title="Install App"
                className={`p-2 rounded-full transition-all animate-bounce-slow ${isDarkMode ? "hover:bg-white/10 text-blue-400 hover:text-blue-300" : "hover:bg-blue-50 text-blue-600 hover:text-blue-700"}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
              </button>
            )}

            <LogoutButton isDarkMode={isDarkMode} />
          </div>
        </div>

        {/* 🔘 TAB SWITCHER (Glassmorphism Pills) */}
        <div className={`flex items-center justify-around px-4 py-3 border-b space-x-2 ${isDarkMode ? 'border-white/5' : 'border-white/10 bg-white/20 backdrop-blur-md'}`}>
          <button
            onClick={() => setActiveTab('chats')}
            className={`flex-1 py-2.5 text-center text-sm font-bold rounded-full transition-all duration-300 transform active:scale-95 ${activeTab === 'chats'
              ? (isDarkMode ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-lg shadow-indigo-500/30' : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/30')
              : (isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-white/40')}`}
          >
            Chats
          </button>
          <button
            onClick={() => setActiveTab('squads')}
            className={`flex-1 py-2.5 text-center text-sm font-bold rounded-full transition-all duration-300 transform active:scale-95 ${activeTab === 'squads'
              ? (isDarkMode ? 'bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white shadow-lg shadow-pink-500/30' : 'bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-lg shadow-pink-500/30')
              : (isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-white/40')}`}
          >
            Squads
          </button>

          <button
            onClick={() => setActiveTab('blink')}
            className={`flex-1 py-2.5 text-center text-sm font-bold rounded-full transition-all duration-300 transform active:scale-95 ${activeTab === 'blink'
              ? (isDarkMode ? 'bg-gradient-to-r from-orange-600 to-amber-600 text-white shadow-lg shadow-orange-500/30' : 'bg-gradient-to-r from-orange-500 to-amber-600 text-white shadow-lg shadow-orange-500/30')
              : (isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-white/40')}`}
          >
            Blink
          </button>

          <button
            onClick={() => setActiveTab('calls')}
            className={`flex-1 py-2.5 text-center text-sm font-bold rounded-full transition-all duration-300 transform active:scale-95 ${activeTab === 'calls'
              ? (isDarkMode ? 'bg-gradient-to-r from-green-600 to-teal-600 text-white shadow-lg shadow-green-500/30' : 'bg-gradient-to-r from-green-500 to-teal-600 text-white shadow-lg shadow-green-500/30')
              : (isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-white/40')}`}
          >
            Calls
          </button>

          <button
            onClick={() => window.open(aiUrl, '_blank')}
            className={`flex-1 py-2.5 text-center text-sm font-bold rounded-full transition-all duration-300 transform active:scale-95 ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-white/40'}`}
          >
            AI 🤖
          </button>
        </div>

        {/* List Content (Animated Transition) */}
        <div className="flex-1 overflow-hidden relative">
          <AnimatePresence mode="wait">
            {activeTab === 'chats' ? (
              <motion.div
                key="chats"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                className="h-full flex flex-col"
              >
                <div className={`p-3 border-b ${isDarkMode ? 'border-white/5 bg-black/10' : 'border-white/10 bg-white/30 backdrop-blur-md'}`}>
                  <AddContact onChatCreated={(user) => {
                    // Ensure convId is set correctly immediately
                    const cid = user.convId || getConvId(currentUid, user.uid);
                    setSelectedUser({ ...user, convId: cid });
                  }} />
                </div>
                <div className={`flex-1 overflow-y-auto custom-scrollbar ${isDarkMode ? 'bg-black/5' : 'bg-white/40 backdrop-blur-lg'}`}>
                  <ConversationsList
                    onSelect={(user) => {
                      // Ensure convId is set correctly
                      const cid = user.convId || getConvId(currentUid, user.uid);
                      setSelectedUser({ ...user, convId: cid });
                    }}
                    activeId={selectedUser?.convId}
                    blockedUsers={currentUserData?.blockedUsers || []}
                    contacts={contacts}
                  />
                </div>
              </motion.div>
            ) : activeTab === 'squads' ? (
              <motion.div
                key="squads"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className={`h-full flex flex-col ${isDarkMode ? 'bg-black/5' : 'bg-white/40 backdrop-blur-lg'}`}
              >
                <div className={`p-3 border-b flex gap-2 ${isDarkMode ? 'border-white/5 bg-black/10' : 'border-white/10 bg-white/30 backdrop-blur-md'}`}>
                  <button
                    onClick={() => setShowCreateSquad(true)}
                    className={`flex-1 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg text-sm font-bold shadow-md hover:shadow-lg transition-all active:scale-95`}
                  >
                    + Create Squad
                  </button>
                  <button
                    onClick={() => setShowJoinSquad(true)}
                    className={`flex-1 py-2 border ${isDarkMode ? 'border-transparent bg-white/10 text-white hover:bg-white/20' : 'border-gray-200 bg-white text-gray-800 shadow-sm hover:bg-gray-50'} rounded-lg text-sm font-bold transition-all active:scale-95`}
                  >
                    Join Squad
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                  <SquadsList
                    onSelect={setSelectedUser}
                    activeId={selectedUser?.uid}
                  />
                </div>
              </motion.div>
            ) : activeTab === 'blink' ? (
              <motion.div
                key="blink"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className={`h-full flex flex-col ${isDarkMode ? 'bg-black/5' : 'bg-white/40 backdrop-blur-lg'}`}
              >
                <BlinkList />
              </motion.div>
            ) : activeTab === 'calls' ? (
              <motion.div
                key="calls"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className={`h-full flex flex-col ${isDarkMode ? 'bg-black/5' : 'bg-white/40 backdrop-blur-lg'}`}
              >
                <div className={`p-4 border-b flex items-center justify-between ${isDarkMode ? 'border-white/5 bg-black/10' : 'border-white/10 bg-white/30 backdrop-blur-md'}`}>
                  <h3 className={`font-bold text-lg ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>Recent Calls</h3>
                </div>
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                  <CallHistoryList />
                </div>
              </motion.div>
            ) : (
              /* AI Tab Sidebar Content */
              <motion.div
                key="ai"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className={`h-full flex flex-col items-center justify-center p-6 text-center ${isDarkMode ? 'bg-black/5 text-gray-400' : 'bg-white/40 text-gray-500'}`}
              >
                <div className="mb-4 text-4xl">🤖</div>
                <h3 className="font-bold text-lg mb-2">AI Assistants</h3>
                <p className="text-sm opacity-70">New AI features coming soon to ChatIQ!</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </aside>

      {/* 🔵 Main Chat Area */}
      <main className={`flex-col h-full relative transition-all duration-300 ${selectedUser || activeTab === 'ai' ? "flex w-full md:flex-1" : "hidden md:flex md:flex-1"} ${isDarkMode ? 'bg-[#0b141a]/90' : 'bg-white/80'}`}>
        {selectedUser?.username ? (
          <>
            <header className={`px-6 py-3 flex justify-between items-center border-b h-[74px] z-20 backdrop-blur-xl ${headerClass} ${isDarkMode ? 'border-white/5' : 'border-gray-200/50'}`}>
              {/* BACK BUTTON for Mobile */}
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedUser(null); }}
                className={`md:hidden mr-3 p-2 rounded-full transition-colors ${isDarkMode ? 'text-white hover:bg-white/10' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
              </button>

              <div className="flex items-center gap-4 cursor-pointer flex-1 min-w-0" onClick={() => setViewImage(selectedUser.photoURL)}>
                <div className="relative shrink-0">
                  {selectedUser.photoURL ? (
                    <img src={selectedUser.photoURL} alt={selectedUser.username} className={`w-11 h-11 rounded-full object-cover border shadow-md ${isDarkMode ? 'border-white/10' : 'border-white'}`} />
                  ) : (
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white text-lg font-bold shadow-lg ${selectedUser.isSquad ? 'bg-gradient-to-br from-indigo-500 to-pink-500' : 'bg-gradient-to-br from-indigo-500 to-purple-600'}`}>
                      {((!selectedUser.isSquad && contacts?.[selectedUser.uid]) || selectedUser.username || "?")[0]?.toUpperCase()}
                    </div>
                  )}
                  {/* Online Dot (Only for Users) */}
                  {!selectedUser.isSquad && selectedUser.isOnline && selectedUser.showLastSeen !== false && (
                    <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full shadow-sm"></span>
                  )}
                </div>

                <div className="flex flex-col justify-center min-w-0 flex-1 px-2">
                  <span className={`font-bold text-base md:text-lg leading-tight tracking-wide truncate ${textClass}`}>
                    {(!selectedUser.isSquad && contacts && contacts[selectedUser.uid]) ? contacts[selectedUser.uid] : selectedUser.username}
                    {selectedUser.isSquad && <span className="ml-2 text-[10px] bg-purple-500 text-white px-1.5 py-0.5 rounded uppercase tracking-wider">Squad</span>}
                  </span>
                  <div className="text-[10px] md:text-xs flex items-center gap-2 font-medium truncate min-h-[1rem]">
                    {selectedUser.isSquad ? (
                      <span className={`${subTextClass} flex flex-wrap items-center gap-x-3 gap-y-2 h-auto mt-0.5`}>
                        <span>{selectedUser.members?.length || 1} members</span>
                        {selectedUser.inviteCode && (
                          <>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-mono tracking-wider opacity-80 select-all border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-gray-100 border-gray-200'}`}>
                              Code: {selectedUser.inviteCode}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const link = `https://chatme.in/join/${selectedUser.inviteCode}`;
                                navigator.clipboard.writeText(link);
                                alert("Invite link copied to clipboard!");
                              }}
                              title="Copy Invite Link"
                              className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold text-white shadow-sm transition-all active:scale-95 bg-gradient-to-r from-blue-500 to-indigo-600 hover:shadow-blue-500/30`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>
                              <span>Copy Link</span>
                            </button>
                            {/* 🆕 QR Code Button */}
                            <button
                              onClick={(e) => { e.stopPropagation(); setShowSquadQR(true); }}
                              title="Show QR Code"
                              className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold text-white shadow-sm transition-all active:scale-95 bg-gradient-to-r from-purple-500 to-pink-500 hover:shadow-pink-500/30`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><rect x="7" y="7" width="3" height="3"></rect><rect x="14" y="7" width="3" height="3"></rect><rect x="7" y="14" width="3" height="3"></rect><rect x="14" y="14" width="3" height="3"></rect></svg>
                              <span>QR</span>
                            </button>
                          </>
                        )}

                      </span>
                    ) : (
                      /* Existing Last Seen/Typing Logic */
                      typing ? <span className="text-[#00a884] font-bold animate-pulse">Typing...</span> : (
                        !selectedUser.isOnline ? (
                          <span className={`${subTextClass} truncate`}>
                            {(() => {
                              if (!selectedUser.lastActive) return "Offline";
                              if (selectedUser.showLastSeen === false) return "";
                              const date = selectedUser.lastActive?.toDate ? selectedUser.lastActive.toDate() : new Date(selectedUser.lastActive);
                              return `Last seen ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                            })()}
                          </span>
                        ) : <span className="text-[#00a884]">Online</span>
                      )
                    )}
                  </div>
                </div>
              </div>

              {/* Actions (Call, More) */}
              <div className="flex items-center gap-2">
                {!selectedUser.isSquad && (
                  <>
                    <button onClick={handleVoiceCall} className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg></button>
                    <button onClick={handleVideoCall} className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 8-6 4 6 4V8Z" /><rect x="2" y="6" width="14" height="12" rx="2" ry="2" /></svg></button>
                  </>
                )}
                <div className="relative">
                  <button onClick={() => setShowChatMenu(!showChatMenu)} className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"></circle><circle cx="12" cy="5" r="1"></circle><circle cx="12" cy="19" r="1"></circle></svg>
                  </button>
                  {showChatMenu && (
                    <div className={`absolute right-0 top-full mt-2 w-48 rounded-lg shadow-xl border overflow-hidden z-50 ${isDarkMode ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-100'}`}>
                      <div className="fixed inset-0 z-40" onClick={() => setShowChatMenu(false)}></div>
                      <div className="relative z-50">
                        <button onClick={() => { clearChat(); setShowChatMenu(false); }} className={`w-full text-left px-4 py-3 text-sm flex gap-2 ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>Clear Chat</button>

                        {!selectedUser.isSquad && (
                          <button onClick={() => {
                            const currentName = contacts?.[selectedUser.uid] || selectedUser.username;
                            const newName = prompt("Edit Contact Name:", currentName);
                            if (newName !== null) {
                              updateContact(selectedUser.uid, newName);
                            }
                            setShowChatMenu(false);
                          }} className={`w-full text-left px-4 py-3 text-sm flex gap-2 ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
                            ✎ Edit Name
                          </button>
                        )}

                        {!selectedUser.isSquad && <button onClick={() => { handleBlockUser(); setShowChatMenu(false); }} className={`w-full text-left px-4 py-3 text-sm flex gap-2 text-red-500 ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>Block User</button>}
                        <button onClick={() => { setShowReportModal(true); setShowChatMenu(false); }} className={`w-full text-left px-4 py-3 text-sm flex gap-2 text-yellow-500 ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>Report</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </header>

            {/* Chat Messages */}
            <div className="flex-1 overflow-hidden relative border-b border-white/5">
              {!isDarkMode && <div className="absolute inset-0 opacity-[0.03] bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>}
              <div className="absolute inset-0 overflow-y-auto custom-scrollbar scroll-smooth z-10">
                <ChatWindow
                  selectedUser={selectedUser}
                  isOnline={selectedUser.isOnline}
                  lastActive={selectedUser.lastActive}
                  setEditingMessage={setEditingMessage}
                  contacts={contacts}
                  onUpdateContact={updateContact}
                />
              </div>
            </div>

            {/* Message Input */}
            <div className={`backdrop-blur-md border-t ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-white/60 border-gray-200'}`}>
              {!selectedUser.isSquad && currentUserData?.blockedUsers?.includes(selectedUser.uid) ? (
                <div className={`p-4 text-center text-sm font-medium ${isDarkMode ? 'text-gray-400 bg-red-900/10' : 'text-gray-500 bg-gray-50'}`}>
                  🚫 You blocked this contact. Unblock to send messages.
                </div>
              ) : (
                <MessageInput
                  convId={selectedUser.convId || selectedUser.uid} // Use UID as convId for Squads
                  editingMessage={editingMessage}
                  setEditingMessage={setEditingMessage}
                  isSquad={selectedUser.isSquad} /* Pass flag to disable encryption */
                  selectedUser={selectedUser} // 👈 Pass Full User Object (with Keys)
                  currentUserData={currentUserData} // 👈 Pass My Data (with Keys)
                />
              )}
            </div>
          </>
        ) : (
          /* Empty State */
          <div className={`flex-1 flex flex-col items-center justify-center text-center p-6 ${isDarkMode ? 'bg-gradient-to-br from-[#0b141a]/95 to-[#1e293b]/90' : 'bg-gradient-to-br from-blue-50/50 to-indigo-50/50'}`}>
            <div className={`w-28 h-28 mb-8 rounded-full flex items-center justify-center shadow-2xl animate-pulse-slow ${isDarkMode ? 'bg-gradient-to-tr from-[#00a884] to-[#007bff] shadow-[#00a884]/20' : 'bg-white shadow-blue-200'}`}>
              <svg className={`${isDarkMode ? 'text-white' : 'text-blue-600'}`} xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
            </div>
            <h1 className={`text-5xl font-bold mb-4 tracking-tight ${textClass}`}>Chat<span className="text-[#00a884]">IQ</span> Web</h1>
            <p className={`text-lg max-w-md leading-relaxed ${subTextClass}`}>
              Connect with friends, family, and squads. <br /> Check out the new <b>Squads</b> tab to join communities!
            </p>
          </div>
        )}
      </main>

      {/* Modals */}
      {showSettings && <Settings onClose={() => setShowSettings(false)} />}
      {showMyProfile && <UserProfileViewer photoURL={currentUserData?.photoURL} onClose={() => setShowMyProfile(false)} />}
      {showBlockedUsers && <BlockedUsers onClose={() => setShowBlockedUsers(false)} />}
      {viewImage && <ImageViewer src={viewImage} alt={selectedUser?.username} onClose={() => setViewImage(null)} />}
      {showStatusSelector && <StatusSelector currentStatus={currentUserData?.status} onClose={() => setShowStatusSelector(false)} />}
      {showCreateSquad && <CreateSquad onClose={() => setShowCreateSquad(false)} />}
      {showJoinSquad && <JoinSquad onClose={() => { setShowJoinSquad(false); setInitialJoinCode(""); }} initialInviteCode={initialJoinCode} />}

      {/* ⚠️ Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center animate-fade-in" onClick={() => setShowReportModal(false)}>
          <div className={`p-6 rounded-2xl shadow-2xl max-w-sm w-[90%] ${isDarkMode ? 'bg-[#1e293b] text-white border border-white/10' : 'bg-white text-gray-900'}`} onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">Report User</h3>
            <div className="space-y-3 mb-4">
              {["Spam", "Harassment", "Inappropriate Content", "Other"].map(r => (
                <label key={r} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="reason"
                    value={r}
                    checked={reportReason === r}
                    onChange={() => setReportReason(r)}
                    className="accent-indigo-500 w-4 h-4"
                  />
                  <span className="text-sm">{r}</span>
                </label>
              ))}
              <textarea
                className={`w-full mt-2 p-3 rounded-lg text-sm outline-none resize-none border ${isDarkMode ? 'bg-black/20 border-white/10 focus:border-indigo-500' : 'bg-gray-50 border-gray-200 focus:border-indigo-500'}`}
                placeholder="Additional details (optional)..."
                rows={3}
                value={reportDetails}
                onChange={e => setReportDetails(e.target.value)}
              ></textarea>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowReportModal(false)} className={`px-4 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}>Cancel</button>
              <button onClick={submitReport} className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-bold shadow-lg shadow-red-500/20">Submit Report</button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Squad QR Modal */}
      {showSquadQR && selectedUser?.isSquad && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center animate-fade-in" onClick={() => setShowSquadQR(false)}>
          <div className={`p-6 rounded-2xl shadow-2xl flex flex-col items-center text-center max-w-sm w-[90%] ${isDarkMode ? 'bg-[#1e293b] text-white border border-white/10' : 'bg-white text-gray-900'}`} onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-1">{selectedUser.username}</h3>
            <p className={`text-xs mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Scan to join this squad</p>

            <div className="bg-white p-4 rounded-xl shadow-inner mb-4">
              <QRCode value={`chatiq:squad:${selectedUser.uid}`} size={200} />
            </div>

            <div className={`text-xs font-mono px-3 py-1 rounded border mb-4 ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-gray-100 border-gray-200'}`}>
              Code: {selectedUser.inviteCode}
            </div>

            <button onClick={() => setShowSquadQR(false)} className="px-6 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold text-sm transition-colors">
              Close
            </button>
          </div>
        </div>
      )}

    </div >
  );
}
